/**
 * Categories page — manage income and expense categories.
 *
 * Two sections: Income categories and Expense categories.
 * Per section: list of categories with delete, plus an inline add affordance.
 *
 * Reachable via the "Categories" nav item at the bottom of the Summary sidebar.
 */
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import {
  useCategoriesQuery,
  useCreateCategoryMutation,
  useDeleteCategoryMutation,
  useRenameCategoryMutation,
} from '@/features/categories/categoryApi';
import type { Category, EntryKind } from '@broke-or-not/shared';
import { useImpersonationStore } from '@/features/admin/impersonationStore';

export const Route = createFileRoute('/_app/categories')({
  component: CategoriesPage,
});

interface CategorySectionProps {
  kind: EntryKind;
  title: string;
}

function CategorySection({ kind, title }: CategorySectionProps) {
  const { data: categories, isLoading } = useCategoriesQuery();
  const createCategory = useCreateCategoryMutation();
  const deleteCategory = useDeleteCategoryMutation();
  const renameCategory = useRenameCategoryMutation();
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  // View-only admin impersonation (quick-260702-qbr): disable add/delete controls while
  // impersonating — the backend already rejects the write with 403 (defense-in-depth).
  const isImpersonating = useImpersonationStore((s) => s.targetSub != null);

  const kindCategories = (categories ?? []).filter((c) => c.kind === kind);

  async function handleAdd() {
    const name = newName.trim();
    if (!name) return;
    try {
      await createCategory.mutateAsync({ name, kind });
      setNewName('');
      toast.success(`Category "${name}" added`);
    } catch {
      toast.error('Could not add category. Please try again.');
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try {
      await deleteCategory.mutateAsync({ id, kind });
      toast.success(`Category "${name}" deleted`);
    } catch {
      toast.error('Could not delete category. Please try again.');
    }
  }

  function handleStartEdit(c: Category) {
    setEditingId(c.id);
    setEditName(c.name);
  }

  function handleCancelEdit() {
    setEditingId(null);
    setEditName('');
  }

  async function handleSaveEdit(c: Category) {
    const name = editName.trim();
    if (!name || name === c.name) {
      handleCancelEdit();
      return;
    }
    try {
      await renameCategory.mutateAsync({ id: c.id, kind, name });
      setEditingId(null);
      toast.success(`Category renamed to "${name}"`);
    } catch {
      toast.error('Could not rename category. Please try again.');
    }
  }

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : kindCategories.length === 0 ? (
          <p className="text-sm text-slate-400">No categories yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {kindCategories.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-2 gap-2">
                {editingId === c.id ? (
                  <div className="flex flex-1 items-center gap-2">
                    <Input
                      autoFocus
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { void handleSaveEdit(c); }
                        if (e.key === 'Escape') { handleCancelEdit(); }
                      }}
                      className="h-8 text-sm"
                      disabled={renameCategory.isPending}
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => { void handleSaveEdit(c); }}
                      disabled={renameCategory.isPending || !editName.trim() || editName.trim() === c.name}
                    >
                      {renameCategory.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Save'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8"
                      onClick={handleCancelEdit}
                      disabled={renameCategory.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <>
                    <span className="text-sm text-slate-700">{c.name}</span>
                    {!isImpersonating && (
                      <div className="flex items-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-blue-500"
                          onClick={() => { handleStartEdit(c); }}
                          aria-label={`Rename ${c.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                          onClick={() => { void handleDelete(c.id, c.name); }}
                          disabled={deleteCategory.isPending}
                          aria-label={`Delete ${c.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Inline add affordance — hidden during view-only admin impersonation */}
        {!isImpersonating && (
          <div className="flex gap-2 pt-1">
            <Input
              placeholder={`New ${kind} category`}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { void handleAdd(); }
              }}
              className="h-9 text-sm"
              disabled={createCategory.isPending}
            />
            <Button
              type="button"
              size="sm"
              className="h-9 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => { void handleAdd(); }}
              disabled={createCategory.isPending || !newName.trim()}
            >
              {createCategory.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Add'
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CategoriesPage() {
  return (
    <div className="mx-auto max-w-[640px] px-4 py-8 sm:px-6 animate-page-in motion-reduce:animate-none">
      <h1 className="mb-6 text-2xl font-semibold tracking-tight text-slate-900">Categories</h1>

      <div className="space-y-6">
        <CategorySection kind="income" title="Income categories" />
        <CategorySection kind="expense" title="Expense categories" />
      </div>
    </div>
  );
}
